import { useState } from "react";
import Button from "../components/button/Button";
import axiosInstance from "../utils/axios-instance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/parse-errors";
import { SubscriptionType } from "../types/subscription-types";

interface ICreateManualSubscriptionModel {
  agencyId: number;
  toggleSubscriptionModel: () => void;
  isSubscription: SubscriptionType;
}

const CreateManualSubscriptionModel = ({
  agencyId,
  toggleSubscriptionModel,
  isSubscription,
}: ICreateManualSubscriptionModel) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<Record<string, any>>({
    plan: "monthly",
  });

  const CreateSubscription = async () => {
    setLoading(true);
    try {
      await axiosInstance.post(
        `/payment/create-manual-subscription/${agencyId}`,
        { ...values }
      );
      toast.success("Subscription Created");
      toggleSubscriptionModel();
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      toggleSubscriptionModel();
      setLoading(false);
    }
  };

  const UpdateSubscription = async () => {
    setLoading(true);
    try {
      await axiosInstance.patch(
        `/payment/manual-subscription/${isSubscription?.id}`,
        { ...values }
      );
      toast.success("Subscription Updated");
      toggleSubscriptionModel();
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      toggleSubscriptionModel();
      setLoading(false);
    }
  };

  const CancelSubscription = async () => {
    setLoading(true);
    try {
      await axiosInstance.put(
        `/payment/super-admin/stripe/cancel-subscription/${agencyId}`,
        { ...values }
      );
      toast.success("Subscription Cancelled");
      toggleSubscriptionModel();
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      toggleSubscriptionModel();
      setLoading(false);
    }
  };

  const HandleOnChange = (e) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const inputClass = "mt-1 w-full h-12 border border-gray-200 rounded-xl px-4 text-[14.5px] outline-none bg-white";
  const labelClass = "block text-[13px] font-medium text-lightBlack";

  return (
    <div className="w-full h-full fixed top-0 bg-black/50 backdrop-blur-sm left-0 z-50 flex items-center justify-center px-4">
      <div className="p-7 rounded-2xl bg-white shadow-pop w-full max-w-[460px] fade-up">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold tracking-tight text-lightBlack">
            {isSubscription?.id ? "Update" : "Create"} Subscription
          </h2>
          <button onClick={toggleSubscriptionModel} className="h-9 w-9 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition" aria-label="Close">
            <FontAwesomeIcon icon={faXmark} className="text-lightBlack text-base" />
          </button>
        </div>

        <div className="w-full mt-6 space-y-4">
          <div>
            <label className={labelClass}>Total Users</label>
            <input
              name="totalUsers"
              defaultValue={values?.totalUsers || isSubscription?.totalUsers}
              onChange={HandleOnChange}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select
              name="status"
              onChange={HandleOnChange}
              defaultValue={values?.status || isSubscription?.status}
              className={inputClass}
            >
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Plan Duration</label>
            <select
              name="plan"
              onChange={HandleOnChange}
              defaultValue={values?.plan || isSubscription?.plan}
              className={inputClass}
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div className="pt-3 space-y-3">
            <Button
              isLoading={loading}
              disabled={loading}
              onClick={isSubscription?.id ? UpdateSubscription : CreateSubscription}
            >
              {isSubscription?.id ? "Update" : "Create"} Subscription
            </Button>

            {isSubscription?.id ? (
              <Button
                className="!bg-red-50 !text-red-600 !border !border-red-100 hover:!bg-red-100"
                variant="secondary"
                isLoading={loading}
                disabled={loading}
                onClick={CancelSubscription}
              >
                Cancel Subscription
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateManualSubscriptionModel;
